import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Broadcast, ChatCircleDots, CaretRight, Clock, Calendar } from "@phosphor-icons/react";

const CONF_START_DATE = "2026-06-22";
const CONF_END_DATE = "2026-06-24";

/**
 * "Now showing" widget - the very first thing delegates see when opening the
 * app during the conference. Surfaces the currently-running session in each
 * room with one-tap-to-watch-live-stream and one-tap-to-submit-question.
 *
 * Auto-switches between four states:
 *   - Pre-conference: countdown "Starts in N days"
 *   - During conf, session(s) live now: per-room "Now showing" cards
 *   - During conf, between sessions: "Up next at HH:MM" peek
 *   - End of conference day with nothing more: encouraging end-of-day card
 *   - Post-conference: hides itself
 *
 * Re-renders the visible state every 30 s by ticking a clock state. Supports a
 * `?demo_time=2026-06-22T09:30` URL override so admins can preview how the
 * widget will look during the talks without waiting for the day.
 */
export default function NowShowingWidget({ sessions, liveStreamStages = {}, stageLabels = {} }) {
  const [searchParams] = useSearchParams();
  const demoTime = searchParams.get("demo_time"); // ISO-ish string

  const [now, setNow] = useState(() => parseNow(demoTime));
  useEffect(() => {
    if (demoTime) return undefined; // frozen demo clock
    const id = setInterval(() => setNow(parseNow(null)), 30_000);
    return () => clearInterval(id);
  }, [demoTime]);

  // Drop any "Break" tracked sessions from consideration - delegates don't
  // need a "Now showing: Morning Tea" widget.
  const realSessions = useMemo(
    () => sessions.filter((s) => (s.track || "").toLowerCase() !== "break"),
    [sessions]
  );

  const state = useMemo(() => deriveState(now, realSessions), [now, realSessions]);

  if (state.kind === "post") return null;
  if (state.kind === "endofday") return null;
  if (state.kind === "live") return null;
  if (state.kind === "upnext") return null;
  if (state.kind === "pre") return <PreConferenceCard days={state.days} demoTime={demoTime} />;

  return (
    <div
      className="mb-4 rounded-sm border border-[#000000]/30 bg-gradient-to-br from-[#000000] to-[#2a1460] text-[#FFFFFF] p-4 sm:p-5 shadow-sm"
      data-testid="now-showing-widget"
      data-state={state.kind}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {state.kind === "live" ? (
            <>
              <span aria-hidden="true" className="relative flex h-2.5 w-2.5">
                <span className="absolute inset-0 rounded-full bg-[#FF5C5C] opacity-75 animate-ping" />
                <span className="relative rounded-full h-2.5 w-2.5 bg-[#FF5C5C]" />
              </span>
              <span className="font-mono-time text-[10px] uppercase tracking-widest">Now showing</span>
            </>
          ) : state.kind === "upnext" ? (
            <>
              <Clock size={14} weight="duotone" />
              <span className="font-mono-time text-[10px] uppercase tracking-widest">Up next · {state.time}</span>
            </>
          ) : (
            <>
              <Calendar size={14} weight="duotone" />
              <span className="font-mono-time text-[10px] uppercase tracking-widest">{state.label}</span>
            </>
          )}
        </div>
        {demoTime && (
          <span className="font-mono-time text-[9px] uppercase opacity-50">DEMO {demoTime.slice(11)}</span>
        )}
      </div>

      {state.kind === "endofday" && (
        <p className="text-sm leading-relaxed opacity-90" data-testid="now-showing-endofday">
          That&apos;s a wrap for today. Catch up via <Link to="/watch" className="underline">live stream replays</Link> or browse the <Link to="/program" className="underline">full programme</Link>.
        </p>
      )}

      {(state.kind === "live" || state.kind === "upnext") && (
        <div className="space-y-2">
          {state.entries.map((entry) => (
            <RoomCard
              key={entry.session.id}
              session={entry.session}
              isLive={state.kind === "live"}
              streamUrl={liveStreamStages[entry.session.stage]}
              stageLabel={stageLabels[entry.session.stage] || entry.session.stage_label || entry.session.stage}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PreConferenceCard({ days, demoTime }) {
  return (
    <div
      className="mb-4 rounded-sm border border-[#000000]/30 bg-gradient-to-br from-[#000000] to-[#2a1460] text-[#FFFFFF] p-4 sm:p-5 flex items-center justify-between gap-4"
      data-testid="now-showing-widget"
      data-state="pre"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Calendar size={28} weight="duotone" className="flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-mono-time text-[10px] uppercase tracking-widest opacity-80">DFTB26 · SEC Glasgow</div>
          <div className="font-display text-base sm:text-lg leading-tight truncate !text-[#FFFFFF]">
            {days === 0 ? "Starts later today" : days === 1 ? "Starts tomorrow" : `Starts in ${days} days`}
          </div>
        </div>
      </div>
      {demoTime && (
        <span className="font-mono-time text-[9px] uppercase opacity-50 hidden sm:inline">DEMO</span>
      )}
    </div>
  );
}

function RoomCard({ session, isLive, streamUrl, stageLabel }) {
  const speakerNames = (session.speakers || []).map((sp) => sp.name).join(", ");
  return (
    <Link
      to={`/program/${session.id}`}
      data-testid={`now-showing-room-${session.id}`}
      className="block bg-[#FFFFFF]/8 hover:bg-[#FFFFFF]/15 transition-colors rounded-sm p-3 group"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono-time text-[10px] uppercase tracking-widest opacity-70">{stageLabel}</span>
            <span className="font-mono-time text-[10px] opacity-50">{session.start_time}-{session.end_time}</span>
            {session.track && (
              <span className="font-mono-time text-[10px] uppercase tracking-widest opacity-90 truncate">
                · {session.track}
              </span>
            )}
          </div>
          <div className="font-display text-base leading-snug truncate !text-[#FFFFFF]">{session.title}</div>
          {speakerNames && (
            <div className="text-xs opacity-80 truncate mt-0.5">{speakerNames}</div>
          )}
        </div>
        <CaretRight size={16} className="opacity-50 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0" />
      </div>

      {isLive && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {streamUrl && (
            <span className="inline-flex items-center gap-1 text-[11px] font-mono-time uppercase tracking-widest px-2 py-1 rounded-sm bg-[#FF5C5C]/20 text-[#FFD6D6]">
              <Broadcast size={12} weight="duotone" /> Live stream
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[11px] font-mono-time uppercase tracking-widest px-2 py-1 rounded-sm bg-[#FFFFFF]/15">
            <ChatCircleDots size={12} weight="duotone" /> Ask a question
          </span>
        </div>
      )}
    </Link>
  );
}

// ---------- pure helpers ----------

function parseNow(demoTime) {
  if (demoTime) {
    const d = new Date(demoTime);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function timeKey(d) {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

function isSameOrBefore(a, b) { return a <= b; } // eslint-disable-line no-unused-vars

function deriveState(now, sessions) {
  const today = dateKey(now);
  const time = timeKey(now);

  if (today < CONF_START_DATE) {
    // Day-level countdown, ignoring time-of-day for simplicity. Clone the Date
    // before mutating it so `now` (held in state) stays untouched.
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const start = new Date(`${CONF_START_DATE}T00:00:00`);
    const days = Math.max(0, Math.ceil((start.getTime() - startOfToday.getTime()) / (24 * 3600 * 1000)));
    return { kind: "pre", days };
  }
  if (today > CONF_END_DATE) {
    return { kind: "post" };
  }

  const todaySessions = sessions.filter((s) => s.date === today);

  // "Live": started within the last 5-min lead-in (treat 5 min before
  // start_time as already counting as live so delegates checking in early get
  // the card), and not yet ended. End-time is STRICT to avoid the edge case
  // where a session ending at 12:30 and one starting at 12:30 both register
  // as live simultaneously.
  const leadInWindow = addMinutes(time, 5);
  const live = todaySessions.filter((s) => {
    const startsSoon = s.start_time <= leadInWindow;
    const stillRunning = time < s.end_time;
    return startsSoon && stillRunning;
  });

  if (live.length > 0) {
    // De-dupe by stage so we get one card per room (a multi-talk block has
    // multiple sessions sharing date/time/stage; pick the first one in title
    // order so the link is deterministic).
    const byStage = new Map();
    for (const s of live.sort((a, b) => (a.title || "").localeCompare(b.title || ""))) {
      if (!byStage.has(s.stage)) byStage.set(s.stage, s);
    }
    const entries = [...byStage.values()].map((session) => ({ session }));
    return { kind: "live", entries };
  }

  const upcoming = todaySessions
    .filter((s) => s.start_time > time)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  if (upcoming.length > 0) {
    const nextTime = upcoming[0].start_time;
    // Show every session starting at that next slot, deduped by stage.
    const byStage = new Map();
    for (const s of upcoming.filter((s) => s.start_time === nextTime).sort((a, b) => (a.title || "").localeCompare(b.title || ""))) {
      if (!byStage.has(s.stage)) byStage.set(s.stage, s);
    }
    const entries = [...byStage.values()].map((session) => ({ session }));
    return { kind: "upnext", time: nextTime, entries };
  }

  // Conference day, but nothing more today.
  return { kind: "endofday", label: "That's a wrap for today" };
}

function subtractMinutes(hhmm, minutes) { // eslint-disable-line no-unused-vars
  return addMinutes(hhmm, -minutes);
}

function addMinutes(hhmm, minutes) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const hh = Math.floor(((total % (24 * 60)) + 24 * 60) % (24 * 60) / 60);
  const mm = ((total % 60) + 60) % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
